import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/ClerkAuthContext";
import { UnauthorizedPage } from "../Common/UnauthorizedPage";
import { apiService } from "../../services/api";
import { ConversationEval } from "./ConversationEval";
import { IntegrationForm } from "./IntegrationForm";
import { DevboxPool } from "./DevboxPool";
import { useNavigate, useLocation } from "react-router-dom";

interface Integration {
  id: string;
  service_name: string;
  client_id: string;
  auth_url: string;
  token_url: string;
  revoke_url?: string;
  scopes: any;
  additional_params: any;
}

export const AdminPage: React.FC = () => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  
  // Determine active tab based on URL
  const getActiveTab = () => {
    if (location.pathname === "/admin/evals") return "conversations";
    if (location.pathname === "/admin/pool") return "pool";
    return "integrations";
  };
  const activeTab = getActiveTab();

  useEffect(() => {
    checkAdminAndLoadIntegrations();
  }, []);

  const checkAdminAndLoadIntegrations = async () => {
    try {
      const token = await getToken();
      const response = await apiService.fetch("/api/admin/status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.isAdmin);
        if (data.isAdmin) {
          await loadIntegrations();
        }
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrations = async () => {
    try {
      const token = await getToken();
      const response = await apiService.fetch("/api/admin/integrations", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIntegrations(data);
      }
    } catch (error) {
      console.error("Failed to load integrations:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <UnauthorizedPage />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-xl rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
            </div>
            {/* Tab Navigation */}
            <div className="mt-4 flex space-x-4">
              <button
                onClick={() => navigate("/admin")}
                className={`px-4 py-2 rounded-t-lg font-medium ${
                  activeTab === "integrations"
                    ? "bg-white border-b-2 border-indigo-600 text-indigo-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Integrations
              </button>
              <button
                onClick={() => navigate("/admin/evals")}
                className={`px-4 py-2 rounded-t-lg font-medium ${
                  activeTab === "conversations"
                    ? "bg-white border-b-2 border-indigo-600 text-indigo-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Conversation Evaluations
              </button>
              <button
                onClick={() => navigate("/admin/pool")}
                className={`px-4 py-2 rounded-t-lg font-medium ${
                  activeTab === "pool"
                    ? "bg-white border-b-2 border-indigo-600 text-indigo-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Devbox Pool
              </button>
            </div>
          </div>

          {activeTab === "pool" ? (
            <DevboxPool />
          ) : activeTab === "integrations" ? (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">Configured Integrations</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Manage OAuth integrations for your platform.
                </p>
              </div>
              {!showCreateForm && !editingIntegration && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Add Integration
                </button>
              )}
            </div>

            {(showCreateForm || editingIntegration) ? (
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-md font-medium mb-4">
                  {editingIntegration ? `Edit Integration: ${editingIntegration.service_name}` : "Create New Integration"}
                </h3>
                <IntegrationForm
                  integration={editingIntegration || undefined}
                  isEdit={!!editingIntegration}
                  onSubmit={async (data) => {
                    const token = await getToken();
                    const url = editingIntegration 
                      ? `/api/admin/integrations/${editingIntegration.service_name}`
                      : "/api/admin/integrations";
                    const method = editingIntegration ? "PUT" : "POST";
                    
                    const response = await apiService.fetch(url, {
                      method,
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify(data),
                    });

                    if (response.ok) {
                      await loadIntegrations();
                      setShowCreateForm(false);
                      setEditingIntegration(null);
                    } else {
                      const error = await response.json();
                      alert(`Failed to save integration: ${error.error}`);
                    }
                  }}
                  onCancel={() => {
                    setShowCreateForm(false);
                    setEditingIntegration(null);
                  }}
                />
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auth URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Token URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scopes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {integrations.map((integration) => (
                    <tr key={integration.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {integration.service_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px]">
                        <span className="font-mono text-xs block truncate" title={integration.client_id}>
                          {integration.client_id.length > 20 ? `${integration.client_id.substring(0, 20)}...` : integration.client_id}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <a
                          href={integration.auth_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-900 text-xs"
                          title={integration.auth_url}
                        >
                          {new URL(integration.auth_url).hostname}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <a
                          href={integration.token_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-900 text-xs"
                          title={integration.token_url}
                        >
                          {new URL(integration.token_url).hostname}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded block truncate" title={JSON.stringify(integration.scopes)}>
                          {JSON.stringify(integration.scopes)}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => setEditingIntegration(integration)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
          ) : (
            <ConversationEval />
          )}
        </div>
      </div>
    </div>
  );
};